"use client";

import clsx from "clsx";
import { useMemo } from "react";

// Seeded random number generator for consistent "random" values
const seededRandom = (seed) => {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
};

const variants = {
  desktop: {
    barCount: 33,
    width: 159,
    height: 32,
    strokeWidth: 2.5,
    gap: 4.877,
    startX: 1.25,
  },
  mobile: {
    barCount: 28,
    width: 99,
    height: 24,
    strokeWidth: 1.8321,
    gap: 3.574,
    startX: 0.916,
  },
};

function WaveSvg({ variant, isPlaying, progress, className }) {
  const { barCount, width, height, strokeWidth, gap, startX } =
    variants[variant];
  const progressX = typeof progress === "number" ? progress * width : null;
  const centerY = height / 2;
  const maxBarHeight = height - 4; // Leave some padding

  // Generate bar properties with seeded randomness for consistent renders
  const bars = useMemo(() => {
    return Array.from({ length: barCount }, (_, index) => {
      const x = startX + index * gap;
      // Different seeds for different properties to feel more random
      const duration = 1.2 + seededRandom(index * 7 + 3) * 2.3; // 1.2s to 3.5s
      const minScale = 0.2 + seededRandom(index * 19) * 0.3; // 0.2 to 0.5 min height
      const baseHeight = 0.3 + seededRandom(index * 23) * 0.7; // Varied base heights

      return { x, duration, minScale, baseHeight };
    });
  }, [barCount, gap, startX]);

  return (
    <svg
      className={className}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <style>
        {`
          @keyframes equalizer {
            0%, 100% { transform: scaleY(1); }
            50% { transform: scaleY(var(--min-scale)); }
          }
        `}
      </style>
      {bars.map(({ x, duration, minScale, baseHeight }, index) => {
        const barHeight = maxBarHeight * baseHeight;
        const y1 = centerY - barHeight / 2;
        const y2 = centerY + barHeight / 2;

        return (
          <line
            key={index}
            x1={x}
            y1={y1}
            x2={x}
            y2={y2}
            stroke={
              progressX !== null
                ? x <= progressX
                  ? "#38362D"
                  : "rgba(56, 54, 45, 0.3)"
                : "#38362D"
            }
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            style={{
              transformOrigin: `${x}px ${centerY}px`,
              "--min-scale": minScale,
              animation: `equalizer ${duration}s ease-in-out infinite`,
              animationPlayState: isPlaying ? "running" : "paused",
            }}
          />
        );
      })}
    </svg>
  );
}

export default function AudioWave({
  className = "",
  isPlaying = false,
  progress,
}) {
  return (
    <div className={clsx("flex-shrink-0", progress !== undefined ? "" : "opacity-50", className )}>
      <WaveSvg
        variant="mobile"
        isPlaying={isPlaying}
        progress={progress}
        className="h-auto w-25 lg:hidden"
      />
      <WaveSvg
        variant="desktop"
        isPlaying={isPlaying}
        progress={progress}
        className="hidden h-auto lg:block lg:w-40"
      />
    </div>
  );
}
